import { RETURN_REQUEST_LEVEL } from '@pperform/pp-constants/widgetConfigs';
import { hasReturnTickets } from '@pperform/pp-utils/parcelOrg';
import { useState } from 'preact/hooks';

import { useGlobalConfigContext } from 'GlobalConfig.context';
import { getEncryptedReturnPk } from 'components/MerchantTrackingWidget/services/ReturnShipment.service';
import type Shipment from 'interfaces/PmParcel';
import { userAuthService } from 'services/userAuth.services';
import { useSearchParams } from 'hooks/useSearchParams';
import { getSearchFormConfigs } from 'services/globalConfig.services';
import { mapLineItemEligibility } from '../../utils';
import { useEvaluateShipmentEligibility } from '../../hooks';

export const useReturnScreen = ({
  shipment,
  onUpdateShipment,
}: {
  shipment: Shipment;
  onUpdateShipment: (shipment: Shipment) => void;
}) => {
  const { updateSearchParams } = useSearchParams();

  const { organizationId, config: configs } = useGlobalConfigContext();
  const { return: returnConfig } = configs;
  const [error, setError] = useState<string>(null);
  const [showValidateConsumerModal, setShowValidateConsumerModal] =
    useState(false);
  const [showRequestReturnModal, setShowRequestReturnModal] = useState(false);

  const { isLoading, isShipmentEligibleForReturn, nonEligibleLineItems } =
    useEvaluateShipmentEligibility(shipment, onUpdateShipment, setError);

  const isNotValidToReturn =
    returnConfig?.returnRequestScope?.returnLevel ===
      RETURN_REQUEST_LEVEL.ORDER && !shipment.orderId;

  const isOrderLevel =
    returnConfig?.returnRequestScope?.returnLevel ===
    RETURN_REQUEST_LEVEL.ORDER;

  const shipmentHasReturnRequests = hasReturnTickets(shipment);

  const redirectToReturnShipment = async () => {
    try {
      const params = isOrderLevel
        ? { orderId: shipment.orderId }
        : { shipmentPk: shipment.poId };

      // call API and get return_id
      const encryptedReturnPk = await getEncryptedReturnPk(
        organizationId,
        params,
      );

      const { searchFields } = getSearchFormConfigs(configs);

      const { urlParam: returnPkUrlParam } = searchFields.find(
        ({ key }) => key === 'return.id',
      );

      updateSearchParams({
        params: { pp_return: 'true', [returnPkUrlParam]: encryptedReturnPk },
        keysToRemove: searchFields.map((item) => item.urlParam),
      });
    } catch (e) {
      setError('ERROR_MSG_GENERAL');
    }
  };

  const handleProcessReturn = () => {
    if (!userAuthService.isUserValidated()) {
      setShowValidateConsumerModal(true);
      return;
    }

    if (shipmentHasReturnRequests) {
      redirectToReturnShipment();
    } else {
      setShowRequestReturnModal(true);
    }
  };

  const handleValidateSucceed = (shipment: Shipment) => {
    onUpdateShipment(mapLineItemEligibility(shipment, nonEligibleLineItems));
    setShowValidateConsumerModal(false);

    if (shipmentHasReturnRequests) {
      redirectToReturnShipment();
    } else {
      setShowRequestReturnModal(true);
    }
  };

  return {
    isLoading,
    error,
    isNotValidToReturn,
    isShipmentEligibleForReturn,
    shipmentHasReturnRequests,
    showValidateConsumerModal,
    showRequestReturnModal,
    handleProcessReturn,
    handleValidateSucceed,
    closeValidateConsumerModal: () => setShowValidateConsumerModal(false),
    closeRequestReturnModal: () => setShowRequestReturnModal(false),
  };
};
